// tabs/SetupTab.jsx - Setup tab
import React, { useState } from 'react';
import { Box, Grid, Stack, TextInput, Group, Text, NumberInput, Paper, Checkbox, Radio } from '@mantine/core';

const SetupTab = ({ quoteData, setQuoteData }) => {
  const [setupData, setSetupData] = useState({
    docMeasuresInfo: {
      weight: 0,
      length: 0,
      volume: 0,
      totalWeight: 0,
      totalLength: 0,
      totalVolume: 0
    },
    convCurrency: {
      code: 'USD',
      name: 'U.S. DOLLAR',
      date: '8/3/2025 5:43:28 PM',
      rate: 1.00000000
    },
    authorizedSignatures: {
      sig1: 'ZKW',
      sig2: '',
      sig3: ''
    },
    options: {
      userLineNumbers: false,
      printDocumentMemo: false,
      printPNMemo1: false,
      printDocumentMessage: true,
      printPNMemo2: false,
      glAcctLineTracking: true,
      aeroxchange: false,
      shipComplete: false,
      copyDocumentImages: false,
      copyLineImages: false
    },
    calculations: {
      discount: { method: 'byPercent', scope: 'doc' },
      freight: { method: 'byDollar', scope: 'doc' },
      vlMcAmx: { method: 'byDollar', scope: 'doc' },
      packaging: { method: 'byDollar', scope: 'line' },
      mtFeeRestock: { method: 'byDollar', scope: 'doc' },
      adgFee: { method: 'byDollar', scope: 'doc' },
      tax: { method: 'byPercent', scope: 'doc' }
    }
  });

  const handleDocMeasureChange = (field, value) => {
    setSetupData(prev => ({
      ...prev,
      docMeasuresInfo: { ...prev.docMeasuresInfo, [field]: value }
    }));
  };

  const handleCurrencyChange = (field, value) => {
    setSetupData(prev => ({
      ...prev,
      convCurrency: { ...prev.convCurrency, [field]: value }
    }));
  };

  const handleSignatureChange = (field, value) => {
    setSetupData(prev => ({
      ...prev,
      authorizedSignatures: { ...prev.authorizedSignatures, [field]: value }
    }));
  };

  const handleOptionChange = (field, value) => {
    setSetupData(prev => ({
      ...prev,
      options: { ...prev.options, [field]: value }
    }));
  };

  const handleCalculationChange = (field, type, value) => {
    setSetupData(prev => ({
      ...prev,
      calculations: {
        ...prev.calculations,
        [field]: { ...prev.calculations[field], [type]: value }
      }
    }));
  };

  return (
    <Box p="md">
      <Grid gutter="md">
        {/* Left Column */}
        <Grid.Col span={6}>
          <Stack spacing="md">
            {/* Doc Measures Info */}
            <Paper withBorder p="sm">
              <Text size="sm" fw={500} mb="sm">Doc Measures Info</Text>
              <Stack spacing="xs">
                <Group spacing="xs" align="flex-end">
                  <Text size="sm" fw={500} w={60}>Weight:</Text>
                  <NumberInput
                    value={setupData.docMeasuresInfo.weight}
                    variant="filled"
                    size="sm"
                    w={80}
                    styles={{ input: { textAlign: 'right', fontWeight: 'bold', color: 'blue' } }}
                    onChange={(value) => handleDocMeasureChange('weight', value)}
                  />
                  <Text size="sm" fw={500} ml="md">Total Weight</Text>
                  <NumberInput
                    value={setupData.docMeasuresInfo.totalWeight}
                    variant="filled"
                    size="sm"
                    w={80}
                    styles={{ input: { textAlign: 'right' } }}
                    onChange={(value) => handleDocMeasureChange('totalWeight', value)}
                  />
                </Group>

                <Group spacing="xs" align="flex-end">
                  <Text size="sm" fw={500} w={60}>Length:</Text>
                  <NumberInput
                    value={setupData.docMeasuresInfo.length}
                    variant="filled"
                    size="sm"
                    w={80}
                    styles={{ input: { textAlign: 'right' } }}
                    onChange={(value) => handleDocMeasureChange('length', value)}
                  />
                  <Text size="sm" fw={500} ml="md">Total Length</Text>
                  <NumberInput
                    value={setupData.docMeasuresInfo.totalLength}
                    variant="filled"
                    size="sm"
                    w={80}
                    styles={{ input: { textAlign: 'right' } }}
                    onChange={(value) => handleDocMeasureChange('totalLength', value)}
                  />
                </Group>

                <Group spacing="xs" align="flex-end">
                  <Text size="sm" fw={500} w={60}>Volume:</Text>
                  <NumberInput
                    value={setupData.docMeasuresInfo.volume}
                    variant="filled"
                    size="sm"
                    w={80}
                    styles={{ input: { textAlign: 'right' } }}
                    onChange={(value) => handleDocMeasureChange('volume', value)}
                  />
                  <Text size="sm" fw={500} ml="md">Total Volume</Text>
                  <NumberInput
                    value={setupData.docMeasuresInfo.totalVolume}
                    variant="filled"
                    size="sm"
                    w={80}
                    styles={{ input: { textAlign: 'right' } }}
                    onChange={(value) => handleDocMeasureChange('totalVolume', value)}
                  />
                </Group>
              </Stack>
            </Paper>

            {/* Conv Currency */}
            <Paper withBorder p="sm">
              <Text size="sm" fw={500} mb="sm">Conv Currency</Text>
              <Stack spacing="xs">
                <Group spacing="xs" align="flex-end">
                  <Text size="sm" fw={500} w={40}>Code</Text>
                  <TextInput
                    value={setupData.convCurrency.code}
                    variant="filled"
                    size="sm"
                    w={60}
                    styles={{ input: { fontWeight: 'bold', color: 'blue' } }}
                    onChange={(e) => handleCurrencyChange('code', e.target.value)}
                  />
                  <TextInput
                    value={setupData.convCurrency.name}
                    variant="filled"
                    size="sm"
                    style={{ flex: 1 }}
                    styles={{ input: { fontWeight: 'bold', color: 'blue' } }}
                    onChange={(e) => handleCurrencyChange('name', e.target.value)}
                  />
                </Group>

                <Group spacing="xs" align="flex-end">
                  <Text size="sm" fw={500} w={40}>Date:</Text>
                  <TextInput
                    value={setupData.convCurrency.date}
                    variant="filled"
                    size="sm"
                    style={{ flex: 1 }}
                    styles={{ input: { fontWeight: 'bold', color: 'blue' } }}
                    onChange={(e) => handleCurrencyChange('date', e.target.value)}
                  />
                </Group>

                <Group spacing="xs" align="flex-end">
                  <Text size="sm" fw={500} w={40}>Rate:</Text>
                  <NumberInput
                    value={setupData.convCurrency.rate}
                    variant="filled"
                    size="sm"
                    style={{ flex: 1 }}
                    styles={{ input: { textAlign: 'right', fontWeight: 'bold', color: 'blue' } }}
                    decimalScale={8}
                    fixedDecimalScale
                    onChange={(value) => handleCurrencyChange('rate', value)}
                  />
                </Group>
              </Stack>
            </Paper>

            {/* Authorized Signatures */}
            <Paper withBorder p="sm">
              <Text size="sm" fw={500} mb="sm">Authorized Signatures</Text>
              <Group spacing="xs" align="flex-end">
                <Text size="sm" fw={500}>1.</Text>
                <TextInput
                  value={setupData.authorizedSignatures.sig1}
                  variant="filled"
                  size="sm"
                  w={80}
                  styles={{ input: { fontWeight: 'bold', color: 'blue' } }}
                  onChange={(e) => handleSignatureChange('sig1', e.target.value)}
                />
                <Text size="sm" fw={500}>2.</Text>
                <TextInput
                  value={setupData.authorizedSignatures.sig2}
                  variant="filled"
                  size="sm"
                  w={80}
                  onChange={(e) => handleSignatureChange('sig2', e.target.value)}
                />
                <Text size="sm" fw={500}>3.</Text>
                <TextInput
                  value={setupData.authorizedSignatures.sig3}
                  variant="filled"
                  size="sm"
                  w={80}
                  onChange={(e) => handleSignatureChange('sig3', e.target.value)}
                />
              </Group>
            </Paper>
          </Stack>
        </Grid.Col>

        {/* Right Column */}
        <Grid.Col span={6}>
          <Stack spacing="md">
            {/* Options */}
            <Paper withBorder p="sm">
              <Text size="sm" fw={500} mb="sm">Options</Text>
              <Grid gutter="xs">
                <Grid.Col span={6}>
                  <Stack spacing="xs">
                    <Checkbox
                      label="User Line Numbers"
                      checked={setupData.options.userLineNumbers}
                      onChange={(e) => handleOptionChange('userLineNumbers', e.currentTarget.checked)}
                    />
                    <Checkbox
                      label="Print P/N Memo 1"
                      checked={setupData.options.printPNMemo1}
                      onChange={(e) => handleOptionChange('printPNMemo1', e.currentTarget.checked)}
                    />
                    <Checkbox
                      label="Print P/N Memo 2"
                      checked={setupData.options.printPNMemo2}
                      onChange={(e) => handleOptionChange('printPNMemo2', e.currentTarget.checked)}
                    />
                    <Checkbox
                      label="Aeroxchange"
                      checked={setupData.options.aeroxchange}
                      onChange={(e) => handleOptionChange('aeroxchange', e.currentTarget.checked)}
                    />
                    <Checkbox
                      label="Copy Document Images"
                      checked={setupData.options.copyDocumentImages}
                      onChange={(e) => handleOptionChange('copyDocumentImages', e.currentTarget.checked)}
                    />
                  </Stack>
                </Grid.Col>
                <Grid.Col span={6}>
                  <Stack spacing="xs">
                    <Checkbox
                      label="Print Document Memo"
                      checked={setupData.options.printDocumentMemo}
                      onChange={(e) => handleOptionChange('printDocumentMemo', e.currentTarget.checked)}
                    />
                    <Checkbox
                      label="Print Document Message"
                      checked={setupData.options.printDocumentMessage}
                      onChange={(e) => handleOptionChange('printDocumentMessage', e.currentTarget.checked)}
                    />
                    <Checkbox
                      label="GL Acct Line Tracking"
                      checked={setupData.options.glAcctLineTracking}
                      onChange={(e) => handleOptionChange('glAcctLineTracking', e.currentTarget.checked)}
                    />
                    <Checkbox
                      label="Ship Complete"
                      checked={setupData.options.shipComplete}
                      onChange={(e) => handleOptionChange('shipComplete', e.currentTarget.checked)}
                    />
                    <Checkbox
                      label="Copy Line Images"
                      checked={setupData.options.copyLineImages}
                      onChange={(e) => handleOptionChange('copyLineImages', e.currentTarget.checked)}
                    />
                  </Stack>
                </Grid.Col>
              </Grid>
            </Paper>

            {/* Calculation Methods */}
            <Paper withBorder p="sm">
              <Stack spacing="xs">
                {Object.entries(setupData.calculations).map(([field, config]) => (
                  <Group key={field} spacing="xs" align="center">
                    <Text size="sm" fw={500} w={120} ta="right">
                      {field === 'vlMcAmx' ? 'V1/MC/AMX' : 
                       field === 'mtFeeRestock' ? 'MT Fee / Restock' :
                       field === 'adgFee' ? 'ADG FEE' :
                       field.charAt(0).toUpperCase() + field.slice(1)}:
                    </Text>
                    <Radio.Group
                      value={config.method}
                      onChange={(value) => handleCalculationChange(field, 'method', value)}
                    >
                      <Group spacing="md">
                        <Radio value="byDollar" label="By $ $" />
                        <Radio value="byPercent" label="By %" />
                      </Group>
                    </Radio.Group>
                    <Radio.Group
                      value={config.scope}
                      onChange={(value) => handleCalculationChange(field, 'scope', value)}
                    >
                      <Group spacing="md">
                        <Radio value="doc" label="Doc" />
                        <Radio value="line" label="Line" />
                      </Group>
                    </Radio.Group>
                  </Group>
                ))}
              </Stack>
            </Paper>
          </Stack>
        </Grid.Col>
      </Grid>
    </Box>
  );
};

export default SetupTab;